import { useState } from "react";

import { ListFilter } from "lucide-react";

import { useAppForm } from "@/components/Form";
import type { BookingApprovalsFilters } from "@/hooks/queries/use-booking-approvals";

import { Button } from "@components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";

import { cn } from "@utils/ui";

import { BookingApprovalsFilterFormData } from "../schema/filters";

const SECURE_AND_SWITCH_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const MATCH_STATUS_OPTIONS = [
  { value: "MATCHED_BY_ID", label: "Matched by ID" },
  { value: "MATCHED_BY_EMAIL", label: "Matched by email" },
  { value: "UNMATCHED", label: "Unmatched" },
];

type FilterBarProps = {
  className?: string;
  onFiltersChange?: (filters: BookingApprovalsFilters) => void;
};

export const FilterBar = ({ className, onFiltersChange }: FilterBarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const form = useAppForm({
    defaultValues: {
      bookingId: "",
      medicalExaminer: "",
      referrer: "",
      examinee: "",
      appointmentDate: "",
      secureAndSwitch: "",
      matchStatus: "",
      hasDocuments: false,
    } as BookingApprovalsFilterFormData,
    onSubmit: async ({ value }) => {
      const filterValues: BookingApprovalsFilters = {
        bookingId: value.bookingId ? Number(value.bookingId) : undefined,
        medicalExaminer: value.medicalExaminer || undefined,
        referrer: value.referrer || undefined,
        examinee: value.examinee || undefined,
        appointmentDate: value.appointmentDate || undefined,
        secureAndSwitch:
          value.secureAndSwitch === "yes"
            ? true
            : value.secureAndSwitch === "no"
              ? false
              : undefined,
        matchStatus: value.matchStatus || undefined,
        hasDocuments: value.hasDocuments || undefined,
      };

      onFiltersChange?.(filterValues);

      const activeFilters = Object.entries(value).some(([key, val]) =>
        key === "hasDocuments"
          ? val === true
          : typeof val === "string" && val.trim() !== ""
      );

      setHasActiveFilters(activeFilters);
      setIsOpen(false);
    },
  });

  const handleCancel = () => {
    form.reset();
    onFiltersChange?.({});
    setHasActiveFilters(false);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {/* Gray simple-outline by default (matches Cancel); switches to the
              blue secondary-subtle variant once filters are active — matching
              CasesList's/DocumentSorter's Filter trigger. */}
          <Button
            variant={hasActiveFilters ? "secondary-subtle" : "simple-outline"}
            size="sm"
            className={cn("h-10 gap-2 transition-all duration-150", className)}
          >
            <ListFilter className="size-4" />
            Filter
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="flex max-h-[var(--radix-popover-content-available-height)] w-96 flex-col overflow-hidden rounded-xl border border-border bg-background-white p-0 shadow-lg"
          align="end"
          side="bottom"
          sideOffset={8}
        >
          <h3 className="flex-shrink-0 border-b border-border px-4 pb-2 pt-4 text-sm font-semibold">
            Filter
          </h3>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-2">
              <form.AppField name="bookingId">
                {(field) => (
                  <field.InputField
                    label="Booking ID"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    type="number"
                    inputMode="numeric"
                    placeholder="e.g. 121808"
                  />
                )}
              </form.AppField>

              <form.AppField name="medicalExaminer">
                {(field) => (
                  <field.InputField
                    label="Medical examiner"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="Enter examiner name"
                  />
                )}
              </form.AppField>

              <form.AppField name="referrer">
                {(field) => (
                  <field.InputField
                    label="Referrer"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="Enter referrer"
                  />
                )}
              </form.AppField>

              <form.AppField name="examinee">
                {(field) => (
                  <field.InputField
                    label="Examinee name"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="Enter examinee name"
                  />
                )}
              </form.AppField>

              <form.AppField name="appointmentDate">
                {(field) => (
                  <field.DateField
                    label="Appointment date"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="Select date"
                    dateFormat="yyyy-MM-dd"
                  />
                )}
              </form.AppField>

              <form.AppField name="secureAndSwitch">
                {(field) => (
                  <field.SelectField
                    label="Secure&Switch"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="All bookings"
                    options={SECURE_AND_SWITCH_OPTIONS}
                  />
                )}
              </form.AppField>

              <form.AppField name="matchStatus">
                {(field) => (
                  <field.SelectField
                    label="Match"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="All bookings"
                    options={MATCH_STATUS_OPTIONS}
                  />
                )}
              </form.AppField>

              <form.AppField name="hasDocuments">
                {(field) => <field.CheckboxField label="Has documents" />}
              </form.AppField>
            </div>

            <form.AppForm>
              <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-border px-4 py-4">
                <button
                  type="button"
                  className="font-head text-xs font-semibold text-foreground-muted underline-offset-2 hover:text-foreground hover:underline"
                  onClick={() => form.reset()}
                >
                  Clear all
                </button>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="simple-outline"
                    size="sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <form.SubmitButton size="sm">Apply filters</form.SubmitButton>
                </div>
              </div>
            </form.AppForm>
          </form>
        </PopoverContent>
      </Popover>
    </div>
  );
};
